import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { Trash, ShieldCheck, User } from "@phosphor-icons/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      setError(formatApiError(e, "Could not load users."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const onDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/admin/users/${deleteId}`);
      setDeleteId(null);
      await reload();
    } catch (e) {
      setError(formatApiError(e, "Could not delete user."));
    }
  };

  return (
    <AppLayout title="Manage Users" eyebrow="Administration">
      {error && <div className="text-sm text-[#993333] mb-4">{error}</div>}

      <div className="border border-[#EAEAE6] bg-white">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#EAEAE6] bg-[#F4F4F0] text-xs uppercase tracking-[0.15em] font-bold text-[#8A8A85]">
          <div className="col-span-4">Name</div>
          <div className="col-span-4">Email</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-1">Joined</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-[#8A8A85]">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-[#8A8A85]">No users yet.</div>
        ) : (
          users.map((u) => (
            <div
              key={u.id}
              className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-[#EAEAE6] last:border-b-0 items-center hover:bg-[#F4F4F0]"
              data-testid={`user-row-${u.id}`}
            >
              <div className="col-span-4 flex items-center gap-3">
                <div className="w-9 h-9 bg-[#1C1C19] text-white flex items-center justify-center font-heading font-bold">
                  {(u.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="font-semibold">{u.name}</div>
              </div>
              <div className="col-span-4 text-sm text-[#3D3D38] truncate">{u.email}</div>
              <div className="col-span-2">
                {u.role === "admin" ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.15em] font-bold bg-[#E25822] text-white">
                    <ShieldCheck size={12} weight="fill" /> Admin
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.15em] font-bold bg-[#EAEAE6] text-[#3D3D38] border border-[#EAEAE6]">
                    <User size={12} /> Employee
                  </span>
                )}
              </div>
              <div className="col-span-1 text-xs text-[#8A8A85] font-mono">
                {u.created_at ? u.created_at.slice(0, 10) : "—"}
              </div>
              <div className="col-span-1 flex justify-end">
                <button
                  onClick={() => setDeleteId(u.id)}
                  disabled={u.role === "admin" || u.id === me?.id}
                  className="p-2 border border-[#EAEAE6] hover:border-[#993333] hover:text-[#993333] disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`delete-user-${u.id}`}
                  title={u.role === "admin" ? "Cannot delete admin" : "Delete"}
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the user and all their progress and enrollments. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-[#993333] hover:bg-[#7E2828] text-white rounded-lg"
              data-testid="confirm-delete-user"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
